import { useRef, useState } from "react";
import { ClickAwayListener } from "@remo-co/ui-core/src/components/ClickAwayListener";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Card } from "@remo-co/ui-core/src/components/Card";
import { Container } from "@remo-co/ui-core/src/components/Container";
import classNames from "classnames";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { selectCurrentEventId } from "modules/event/selectors";
import { IEvent } from "modules/event/types";
import { IEventDirectoryPopulated } from "modules/eventDirectory/types";
import { selectIsMobile } from "modules/deviceInfo";
import { Building05 as Building05Icon } from "@remo-co/ui-core/src/icons/Building05";
import { useStyles } from "./index.styles";
import Menu from "../Menu";
import PreviewMenu from "./PreviewMenu";
import FirstTimeUserNudgePopover from "./FirstTimeUserNudgePopover";

interface IEventSwitcherProps {
  eventDirectory?: IEventDirectoryPopulated;
  title: string;
  previewName?: string;
  previewEvents?: (IEvent | null)[];
  presentationMode?: boolean;
}

const EventSwitcher = ({
  eventDirectory,
  title,
  previewName,
  previewEvents,
  presentationMode,
}: IEventSwitcherProps): JSX.Element => {
  const { t } = useI18n(["eventDirectory"]);
  const rootRef = useRef<null | HTMLDivElement>(null);
  const buttonRef = useRef<null | HTMLButtonElement>(null);
  const styles = useStyles();
  const isMobileView = useSelector(selectIsMobile);
  const [isMenuVisible, setMenuVisible] = useState(false);
  const currentEventId = useSelector(selectCurrentEventId);

  const onClick = () => {
    setMenuVisible(!isMenuVisible);
  };
  const onClickAway = () => {
    setMenuVisible(false);
  };

  return (
    <ClickAwayListener onClickAway={onClickAway} mouseEvent="onMouseUp">
      <Container
        ref={rootRef}
        className={classNames({
          [styles.rootContainerMobileMenuVisible]:
            isMobileView && !presentationMode && isMenuVisible,
          [styles.rootContainerMobilePresentationModeMenuVisible]:
            isMobileView && presentationMode && isMenuVisible,
        })}
      >
        <Card
          size="xs"
          flex={false}
          fullWidth={false}
          className={classNames({
            // desktop web styles
            [styles.container]: !isMobileView,
            [styles.containerPresentationMode]:
              !isMobileView && presentationMode,
            [styles.containerMenuVisible]:
              !isMobileView && (isMenuVisible || previewEvents),
            // mobile styles
            [styles.containerMobile]: isMobileView,
            [styles.containerMobileMenuVisible]: isMobileView && isMenuVisible,
            [styles.containerMobilePresentationMode]:
              isMobileView && presentationMode,
            [styles.containerMobilePresentationMenuVisible]:
              isMobileView && presentationMode && isMenuVisible,
            // preview
            [styles.preview]: previewEvents,
          })}
          innerClassName={styles.innerContainer}
          onClick={onClick}
        >
          <Container
            className={classNames(styles.iconContainer, {
              [styles.iconMobile]: isMobileView && !isMenuVisible,
            })}
          >
            <Building05Icon />
          </Container>
          <Container>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classNames(styles.directoryName, {
                [styles.directoryNamePresentationMode]: presentationMode,
                [styles.directoryNameMobile]: isMobileView,
                [styles.textMobilePresentation]:
                  isMobileView && presentationMode,
                [styles.textMobileMenuVisible]: isMobileView && isMenuVisible,
              })}
            >
              {previewName ?? eventDirectory?.name}
            </Typography>
            <Typography
              variant="h5"
              color="textPrimary"
              className={classNames(styles.eventName, {
                [styles.eventNamePresentationMode]: presentationMode,
                [styles.eventNameMobile]: isMobileView,
                [styles.textMobilePresentation]:
                  isMobileView && presentationMode,
                [styles.textMobileMenuVisible]: isMobileView && isMenuVisible,
              })}
            >
              {title}
            </Typography>
          </Container>
          <Button
            color="primary"
            size="md"
            variant="secondary"
            ref={buttonRef}
            data-testid="event-switcher"
            className={classNames(styles.button, {
              [styles.buttonPresentationMode]: presentationMode,
            })}
            contentClassName={classNames(styles.buttonContent, {
              [styles.buttonContentPresentationMode]: presentationMode,
            })}
            contentWrapperClassName={styles.contentWrapperClassName}
          >
            {!isMobileView ? t("eventDirectory:events") : ""}
          </Button>
          {!previewEvents && currentEventId && eventDirectory && (
            <Menu
              containerRef={rootRef}
              eventDirectory={eventDirectory}
              currentEventId={currentEventId}
              isMenuVisible={isMenuVisible}
              presentationMode={presentationMode}
            />
          )}
          {!previewEvents && (
            <FirstTimeUserNudgePopover
              containerRef={buttonRef}
              presentationMode={presentationMode}
            />
          )}
        </Card>
        {previewEvents && (
          <PreviewMenu
            currentEventId={currentEventId}
            events={previewEvents.slice(1)}
          />
        )}
      </Container>
    </ClickAwayListener>
  );
};

export default EventSwitcher;
